import React, { FC, useEffect } from 'react'
import { GridOuter, LogoContainer, LogosList, SponsorsLogoGrid, StyledGrid } from './style'

//* Helpers
import ImgUtil from '../../../utils/img.utils'
import WrapperImage from '../wrapperImage'

//* Constants
import { SPONSOR_USECASES, UseCases } from '../../../constants/useCases.constants'
import { ComponentGrid } from '../../../models/components/grid'
import { WrapperImage as WrapperImageModel } from '../../../models/others/image'
import Typography from '../typography'

type Props = {
  data: ComponentGrid
  isHomePage?: boolean
}

const Grid: FC<Props> = ({ data, isHomePage }) => {
  if (!data) return null

  const gridData = {
    logos: data?.logos || [],
    topPadding: data?.topPadding || 'None',
    bottomPadding: data?.bottomPadding || 'Large',
    theme: data?.theme || 'Light',
    useCase: data?.useCase || UseCases.GRID_CENTERED,
    rowMax: data?.rowMax || 'None',
    sponsorLogos: data?.sponsorLogos || null,
    title: data?.title || '',
  }

  let gridItemClasses = ''
  let gridItemImgWidth: number, gridItemImgHeight: number

  if (gridData.logos.length === 3) {
    gridItemClasses = 'Grid__Item--LG'
    gridItemImgWidth = 384
    gridItemImgHeight = 192
  } else if (gridData.logos.length === 4) {
    gridItemClasses = 'Grid__Item--MD'
    gridItemImgWidth = 282
    gridItemImgHeight = 141
  } else if (gridData.logos.length >= 5) {
    gridItemClasses = 'Grid__Item--SM'
    gridItemImgWidth = 180
    gridItemImgHeight = 90
  }

  // Sponsor logos Grid Layout Functionality
  const setLastRowClass = () => {
    // Set the last row class for the sponsor logos
    if (typeof window !== 'undefined') {
      const sponsorLogoMap = {
        [SPONSOR_USECASES.DIAMOND]: [1, 2, 3],
        [SPONSOR_USECASES.RUBY]: [2, 3, 4],
        [SPONSOR_USECASES.EMERALD]: [3, 4, 5],
        [SPONSOR_USECASES.SAPPHIRE]: [3, 4, 6],
      } as const

      const screenIndex = window.innerWidth < 768 ? 0 : window.innerWidth < 1024 ? 1 : 2
      const sponsorClass = gridData.sponsorLogos
      const logoPerRow = sponsorClass ? sponsorLogoMap[sponsorClass]?.[screenIndex] ?? 0 : 0

      if (typeof sponsorClass !== 'undefined' && typeof logoPerRow === 'number') {
        const itemsContainer = document.querySelectorAll(`.${sponsorClass}`)
        itemsContainer.forEach(item => {
          const items = item.querySelectorAll('.Logos__List > div') as NodeListOf<HTMLElement>
          const total = items.length
          const start = Math.floor((total - 1) / logoPerRow) * logoPerRow
          items.forEach((item, i) => {
            item.classList.toggle('lastRow', i >= start)
          })
        })
      }
    }
  }

  useEffect(() => {
    setLastRowClass()

    if (document.readyState === 'complete') {
      setLastRowClass()
    } else {
      window.addEventListener('load', setLastRowClass)
    }

    window.addEventListener('resize', setLastRowClass)
    return () => {
      window.removeEventListener('load', setLastRowClass)
      window.removeEventListener('resize', setLastRowClass)
    }
  }, [])

  return (
    <GridOuter
      $gridContainerTheme={gridData.theme}
      $topPadding={gridData.topPadding}
      $bottomPadding={gridData.bottomPadding}
      $isHomePage={isHomePage}
      data-cy="grid">
      {!gridData.sponsorLogos ? (
        <StyledGrid
          $useCase={gridData.useCase}
          $rowMax={gridData.rowMax}
          className={gridData.useCase === 'Full width' ? 'fullWidth__useCase' : ''}>
          {gridData.logos?.map((logo: WrapperImageModel, i: number) => {
            const alt = logo?.altText || ''
            const url = logo?.image?.file?.url || ''

            if (url) {
              return (
                <LogoContainer
                  className={`${gridItemClasses} ${
                    gridData.useCase === 'Full width' ? 'fullWidth__useCase--img' : ''
                  }`}
                  key={i}>
                  <WrapperImage
                    alt={alt}
                    src={ImgUtil.getResizedImageUrl(url, '')}
                    threshold={650}
                  />
                </LogoContainer>
              )
            }
          })}
        </StyledGrid>
      ) : (
        <SponsorsLogoGrid>
          <div className={gridData.sponsorLogos}>
            <Typography type="header3">{gridData.title}</Typography>
            <LogosList
              className="Logos__List"
              $sponsor={gridData.sponsorLogos}
              $rowMax={gridData.rowMax}>
              {gridData.logos?.map((logo: WrapperImageModel, i: number) => {
                const alt = logo?.altText || ''
                const url = logo?.image?.file?.url || ''

                if (url) {
                  return (
                    <LogoContainer key={i}>
                      <WrapperImage
                        alt={alt}
                        src={ImgUtil.getResizedImageUrl(url, '')}
                        threshold={650}
                      />
                    </LogoContainer>
                  )
                }
              })}
            </LogosList>
          </div>
        </SponsorsLogoGrid>
      )}
    </GridOuter>
  )
}

export default Grid
